import moment from "moment";
import { uuid } from "uuidv4";
import { TFunction } from "i18next";

import {
    API_PROJECT_URL,
    API_DUPLICATE_URL,
    API_DISSOCIATE_URL,
    PROJECT_KEY,
    DATE_FORMAT,
    HOUR_FORMAT,
    ActionType,
} from "./constants";
import { ProjectPayload, DissociatePayload, ProjectRow } from "./types";
import { shopsPos } from "./helpers";

interface FetchGlobalFn {
    (url: string, options: RequestInit): Promise<{ error?: boolean; message?: string }>;
}

function buildProjectPayload(
    base: Omit<ProjectPayload, "data" | "type">,
    type: string,
    dataProject: Record<string, unknown>
): ProjectPayload {
    return {
        ...base,
        type,
        data: {
            [PROJECT_KEY]: {
                id_project: PROJECT_KEY,
                data_project: dataProject,
            },
        },
    };
}

function basePayload(params: {
    userID: string;
    franchiseID: string;
    operatorID: string;
    shopId: string[];
}) {
    return {
        userId: params.userID,
        uuidUser: localStorage.getItem("uuidUser"),
        shopId: params.shopId,
        franchiseId: params.franchiseID,
        operatorId: params.operatorID,
    };
}

function postJSON(fetchGlobal: FetchGlobalFn, url: string, payload: unknown) {
    return fetchGlobal(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
}

export async function addProject(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        shopId: string;
        shopName: string;
        designation: string;
        remark: string;
        template?: Record<string, unknown>;
    }
) {
    const id = uuid();
    const dataShop: Record<string, unknown> = {};
    if (params.oneShop) {
        dataShop[params.shopId] = {
            designation: params.shopName,
            id_boutique: params.shopId,
        };
    }

    const payload = buildProjectPayload(
        basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: params.oneShop ? [params.shopID, "0"] : ["0"],
        }),
        ActionType.ADD,
        {
            [id]: {
                projectId: id,
                template: params.template ?? {},
                associates: params.oneShop ? dataShop : {},
                designation: params.designation,
                shopId: params.shopId,
                note: params.remark,
                status: "En attente",
                isArchived: false,
                modified_at: moment().format(DATE_FORMAT),
                hour: moment().format(HOUR_FORMAT),
            },
        }
    );

    return postJSON(fetchGlobal, API_PROJECT_URL, payload);
}

export async function editProject(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        shopId: string;
        designation: string;
        remark: string;
        selectedRows: ProjectRow;
        t: TFunction;
    }
) {
    const ids = shopsPos(params.selectedRows);

    const payload = buildProjectPayload(
        basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: params.oneShop ? ["0", params.shopID] : ids,
        }),
        ActionType.MODIFY,
        {
            [params.selectedRows.projectId]: {
                projectId: params.selectedRows.projectId,
                template: params.selectedRows.template,
                associates: params.selectedRows.associates,
                designation: params.designation,
                note: params.remark,
                status: (params.selectedRows as Record<string, unknown>)[params.t("Status")],
                isArchived: false,
                shopId: params.shopId,
                modified_at: moment().format(DATE_FORMAT),
                hour: moment().format(HOUR_FORMAT),
            },
        }
    );

    return postJSON(fetchGlobal, API_PROJECT_URL, payload);
}

export async function archiveProjects(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        selectedRows: ProjectRow[];
        t: TFunction;
    }
) {
    const dataProject: Record<string, unknown> = {};

    params.selectedRows.forEach((row) => {
        if (row.id_boutique === undefined) {
            dataProject[row.projectId] = {
                projectId: row.projectId,
                template: row.template,
                associates: row.associates,
                designation: (row as Record<string, unknown>)[params.t("Designation")],
                note: (row as Record<string, unknown>)[params.t("Note")],
                status: (row as Record<string, unknown>)[params.t("Status")],
                isArchived: true,
                shopId: row.shopId,
                modified_at: moment().format(DATE_FORMAT),
                hour: moment().format(HOUR_FORMAT),
            };
        }
    });

    if (Object.keys(dataProject).length === 0) return null;

    const payload = buildProjectPayload(
        basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: ["0"],
        }),
        ActionType.ARCHIVE,
        dataProject
    );

    return postJSON(fetchGlobal, API_PROJECT_URL, payload);
}

export async function unarchiveProjects(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        selectedRows: ProjectRow[];
        t: TFunction;
    }
) {
    const dataProject: Record<string, unknown> = {};

    params.selectedRows.forEach((row) => {
        dataProject[row.projectId] = {
            projectId: row.projectId,
            template: row.template,
            associates: row.associates,
            designation: (row as Record<string, unknown>)[params.t("Designation")],
            note: (row as Record<string, unknown>)[params.t("Note")],
            status: (row as Record<string, unknown>)[params.t("Status")],
            isArchived: false,
            shopId: row.shopId,
            modified_at: moment().format(DATE_FORMAT),
            hour: moment().format(HOUR_FORMAT),
        };
    });

    const payload = buildProjectPayload(
        basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: ["0"],
        }),
        ActionType.UNARCHIVE,
        dataProject
    );

    return postJSON(fetchGlobal, API_PROJECT_URL, payload);
}

export function generateDuplicateName(
    originalName: string,
    t: TFunction
): string {
    return `${originalName}_${t("Copy")}_${moment().format("DD/MM/YY - HH:mm:ss")}`;
}

export async function duplicateProject(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: number;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        row: ProjectRow;
        t: TFunction;
        designation?: string;
    }
) {
    const id = uuid();
    const ids = shopsPos(params.row);
    const designation = params.designation
        ?? generateDuplicateName((params.row as Record<string, unknown>)[params.t("Designation")] as string, params.t);

    const payload = {
        ...basePayload({
            userID: params.userID,
            franchiseID: params.franchiseID,
            operatorID: params.operatorID,
            shopId: params.oneShop ? [params.shopID, "0"] : ids,
        }),
        type: ActionType.DUPLICATE,
        itemProject: params.row.projectId,
        data: {
            [PROJECT_KEY]: {
                id_project: PROJECT_KEY,
                data_project: {
                    [id]: {
                        projectId: id,
                        template: params.row.template,
                        associates: params.row.associates,
                        designation,
                        note: (params.row as Record<string, unknown>)[params.t("Note")],
                        status: "en attente",
                        isArchived: false,
                        shopId: params.row.shopId,
                        modified_at: moment().format(DATE_FORMAT),
                        hour: moment().format(HOUR_FORMAT),
                    },
                },
            },
        },
    };

    return postJSON(fetchGlobal, API_DUPLICATE_URL, payload);
}

export async function dissociateProject(
    fetchGlobal: FetchGlobalFn,
    params: {
        userID: string;
        franchiseID: string;
        operatorID: string;
        oneShop: boolean;
        shopID: string;
        selectedRows: ProjectRow[];
    }
) {
    const arrayOfRows = params.selectedRows.filter(
        (row) => row.projectId !== undefined
    );

    if (arrayOfRows.length === 0) return null;

    const grouped = arrayOfRows.reduce<Record<string, { designation: string; shops: { shopId: string; shopName: string }[] }>>((acc, row) => {
        const shop = params.oneShop ? params.shopID : (row.id_boutique ?? "");
        const shopName = (row as Record<string, unknown>).Désignation as string ?? "";
        const designation = row.Project ?? "";

        if (!acc[row.projectId]) {
            acc[row.projectId] = { designation, shops: [] };
        }
        acc[row.projectId].shops.push({ shopId: shop, shopName });
        return acc;
    }, {});

    const rows = Object.entries(grouped).map(
        ([projectId, value]) => ({
            projectId,
            designation: value.designation,
            shopKeys: value.shops,
        })
    );

    const savedData: DissociatePayload = {
        userId: params.userID,
        uuidUser: localStorage.getItem("uuidUser"),
        franchiseId: params.franchiseID,
        operatorId: params.operatorID,
        projectKey: PROJECT_KEY,
        rows,
    };

    return postJSON(fetchGlobal, API_DISSOCIATE_URL, savedData);
}
